import type { AccountInfo } from '@features/trades/hooks/useTradeMetadata';

/**
 * Pure account↔connection derivations for the Accounts panel + the topbar AccountConnectionControl,
 * extracted `cqgUiState.ts`-style so the tricky windows (live-before-accounts, other-login mismatch,
 * this-tab socket gating) stay pinned by unit tests with no React/socket mocking.
 *
 * Model (2026-07 redesign): one credential set exposes MULTIPLE brokerage accounts; the journal
 * accumulates accounts across different logins over time; at most ONE live session per platform.
 * An account is "live" iff its provider's session is live AND its id is in that session's
 * owned-account set (`status.accounts` — the same strings as `AccountInfo.name`).
 */

/** Provider display state — `deriveBadgeDisplay` output shape (CqgBeState / RithmicBeState). */
export type ProviderDisplay = 'disconnected' | 'connecting' | 'live' | 'error';

/** One provider's connection snapshot, as exposed by useCqgDisplayState / useRithmicDisplayState. */
export interface ProviderSnapshot {
  display: ProviderDisplay;
  /** Account ids owned by the live session (empty when none / not yet reported). */
  accounts: string[];
  /** BE state reason — 'concurrent_session' is the actionable one. */
  reason: string | null;
}

const OFFLINE_SNAPSHOT: ProviderSnapshot = { display: 'disconnected', accounts: [], reason: null };

function snapshotFor(provider: AccountInfo['provider'], cqg: ProviderSnapshot, rithmic: ProviderSnapshot): ProviderSnapshot | null {
  if (provider === 'cqg') return cqg;
  if (provider === 'rithmic') return rithmic;
  return null; // imported / manual — no live session concept
}

/**
 * TRUE while the provider says `live` but its owned-accounts list is still empty. CQG publishes
 * `cqg.state=live` on LogonResult BEFORE the AccountsReport fills the list (a separate push a beat
 * later), and right after a refresh `live` is socket-gated back to `connecting` — either way an empty
 * list must NEVER be read as "this account belongs to a different login". Callers treat this window
 * as `connecting`, not as offline/mismatch.
 */
export function isAwaitingAccounts(p: ProviderSnapshot): boolean {
  return p.display === 'live' && p.accounts.length === 0;
}

export type AccountRowState = 'live' | 'connecting' | 'offline';

export interface AccountConnectionInfo {
  /** Provider session live AND it owns this account id. */
  isLive: boolean;
  /** Row chip state: live → green chip; connecting → provider mid-handshake (incl. the empty-accounts window). */
  rowState: AccountRowState;
  /** Whether a Link/Unlink action exists at all (cqg/rithmic — imported/manual have no connect flow). */
  linkable: boolean;
}

/**
 * Per-row derivation for the Accounts panel.
 *
 * `belongsToActiveConnect` scopes the SESSION's connecting/awaiting state to the accounts that
 * actually belong to the login being connected (2026-07-18 fix). A CQG login owns a SUBSET of the
 * user's accounts — a demo login and a live login own different books — but there is ONE CQG session,
 * so without scoping every CQG row would flash "connecting" whenever any CQG login connects. Once the
 * session is LIVE its `accounts` list is definitive (`includes`), so this only affects the
 * connecting/awaiting window. Callers compute it from the active connect's environment + the
 * per-account login memory; the default `true` preserves the un-scoped behavior for callers/tests
 * that don't distinguish (and is correct when there's a single login).
 */
export function deriveAccountConnection(
  account: Pick<AccountInfo, 'name' | 'provider'>,
  cqg: ProviderSnapshot,
  rithmic: ProviderSnapshot,
  belongsToActiveConnect = true,
): AccountConnectionInfo {
  const p = snapshotFor(account.provider, cqg, rithmic);
  if (!p) return { isLive: false, rowState: 'offline', linkable: false };
  const isLive = p.display === 'live' && p.accounts.includes(account.name);
  if (isLive) return { isLive: true, rowState: 'live', linkable: true };
  // Session mid-handshake: only accounts that belong to the login being connected show "connecting";
  // an unrelated login's accounts stay offline instead of a false "connecting".
  if ((p.display === 'connecting' || isAwaitingAccounts(p)) && belongsToActiveConnect) {
    return { isLive: false, rowState: 'connecting', linkable: true };
  }
  return { isLive: false, rowState: 'offline', linkable: true };
}

/**
 * Dropdown/topbar label for one account, MOVED from the deleted `AccountDropdown` and extended with
 * the Rithmic branch. Broker-backed accounts are prefixed and carry their brokerage account number so
 * the user can tell accounts apart at a glance ("CQG: Jane Doe 9990001"); when the provider
 * hasn't populated a friendly name yet (displayName == name) the duplicate is dropped to avoid
 * "CQG: 9990001 9990001". Imported accounts show their plain name.
 */
export function formatAccountLabel(a: AccountInfo): string {
  const { prefix, name } = formatAccountLabelParts(a);
  return prefix ? `${prefix}: ${name}` : name;
}

/**
 * The label split into its broker prefix and the rest (2026-09-02 topbar Figma: the prefix renders
 * BOLD — "**CQG:** Simcgxl Pscqgxl" — so the control needs the two halves, not one string).
 * `prefix` is null for imported/manual accounts, which have no broker to name.
 */
export function formatAccountLabelParts(a: AccountInfo): { prefix: string | null; name: string } {
  if (a.provider === 'cqg' || a.provider === 'rithmic') {
    const prefix = a.provider === 'cqg' ? 'CQG' : 'RITHMIC';
    const num = a.brokerageAccountNumber || a.name;
    return {
      prefix,
      name: a.displayName && a.displayName !== a.name ? `${a.displayName} ${num}` : num,
    };
  }
  return { prefix: null, name: a.displayName || a.name };
}

export interface SelectedAccountState {
  /** Topbar control chip: LIVE / CONNECTING / ERROR / OFFLINE (of the SELECTED account). */
  display: 'live' | 'connecting' | 'error' | 'offline';
  /**
   * The selected account's provider session is live under a DIFFERENT login (its owned-accounts list
   * is populated and does not contain this account) — surfaced as OFFLINE + a "reconnect with that
   * login" hint, never a false live.
   */
  mismatch: boolean;
  /** Control label — `formatAccountLabel`, with the old AccountDropdown pending/empty fallbacks. */
  label: string;
  /** The label's broker prefix ("CQG" / "RITHMIC"), rendered bold by the control; null when the
   *  account has none (imported) or no account is resolved. `labelName` is the remainder. */
  labelPrefix: string | null;
  labelName: string;
  /** The CQG gateway environment the selected account lives on (2026-09-02 topbar Figma: a LIVE or
   *  DEMO pill on the control). Null for accounts without one — Rithmic, imported, unresolved. */
  environment: 'demo' | 'live' | null;
}

export function deriveSelectedAccountState(
  selectedName: string,
  accounts: AccountInfo[] | undefined,
  accountsPending: boolean,
  cqg: ProviderSnapshot,
  rithmic: ProviderSnapshot,
  selectedBelongsToActiveConnect = true,
): SelectedAccountState {
  const info = accounts?.find(a => a.name === selectedName);
  const fallbackLabel =
    selectedName || (accountsPending ? 'Account' : (accounts?.length ?? 0) === 0 ? 'Setup account' : 'Account');
  const parts = info ? formatAccountLabelParts(info) : { prefix: null, name: fallbackLabel };
  const base = {
    label: info ? formatAccountLabel(info) : fallbackLabel,
    labelPrefix: parts.prefix,
    labelName: parts.name,
    // Only CQG has a demo/live GATEWAY — a stray environment on any other provider's row is noise,
    // never a pill.
    environment: info?.provider === 'cqg' ? info.environment : null,
  };

  const p = info ? snapshotFor(info.provider, cqg, rithmic) : null;
  if (!info || !p) return { ...base, display: 'offline', mismatch: false };

  if (p.display === 'error') return { ...base, display: 'error', mismatch: false };
  // Scope the connecting/awaiting state to the selected account too (see deriveAccountConnection):
  // a demo connect must not show a selected LIVE account as "connecting".
  if (p.display === 'connecting' || isAwaitingAccounts(p)) {
    return selectedBelongsToActiveConnect
      ? { ...base, display: 'connecting', mismatch: false }
      : { ...base, display: 'offline', mismatch: false };
  }
  if (p.display === 'live') {
    if (p.accounts.includes(info.name)) return { ...base, display: 'live', mismatch: false };
    return { ...base, display: 'offline', mismatch: true };
  }
  return { ...base, display: 'offline', mismatch: false };
}

/**
 * Whether an account belongs to the login CURRENTLY being connected (pure helper for the hook).
 *
 * When the account remembers which username last connected it, that username MUST match — the
 * environment is only a fallback for legacy rows with no login memory. Rithmic has no environment
 * split and no recorded per-account login, so it stays un-scoped (true). Non-broker rows never
 * connect.
 *
 * This used to be `envMatch || loginMatch`, which made an environment match on its own sufficient.
 * With more than one CQG login in the same environment that lights up every one of their rows at
 * once — the user saw two accounts reading CONNECTING simultaneously and reasonably read it as two
 * concurrent sessions (2026-08-07: "only one connection at a time"). There is only ever one CQG
 * session; the second row was a display artifact. Requiring the remembered username to match makes
 * the state say what is actually true, and matches the strictness `accountOwnedByLogin` already used
 * for the reconnect popup, for the same reason: claiming an account connected under a DIFFERENT
 * username tells the user the wrong thing.
 */
export function accountBelongsToActiveConnect(
  account: Pick<AccountInfo, 'name' | 'provider' | 'environment'>,
  active: { environment: 'demo' | 'live' | null; username: string | null },
  rememberedUsername: string | null,
): boolean {
  if (account.provider === 'rithmic') return true;
  if (account.provider !== 'cqg') return false;
  if (rememberedUsername !== null) {
    return !!active.username && rememberedUsername === active.username;
  }
  return account.environment != null && account.environment === active.environment;
}

/**
 * Does this CQG account belong to the given LOGIN (username + demo/live)? Used by the reconnect
 * popup (2026-07-31 "elaborate ... so I'll know to which CQG should I connect") to NAME the accounts
 * a take-over signs in when the user has several CQG accounts. Stricter than
 * `accountBelongsToActiveConnect`: when the account has a remembered username it must MATCH — the
 * env-only fallback is used solely for legacy rows with no login memory, because claiming an account
 * that was connected under a DIFFERENT username (same environment) would tell the user the wrong
 * thing about what the reconnect touches.
 */
export function accountOwnedByLogin(
  account: Pick<AccountInfo, 'provider' | 'environment'>,
  login: { environment: 'demo' | 'live' | null; username: string | null },
  rememberedUsername: string | null,
): boolean {
  if (account.provider !== 'cqg') return false;
  if (rememberedUsername !== null) return !!login.username && rememberedUsername === login.username;
  return account.environment != null && account.environment === login.environment;
}

export interface ProviderAlert {
  provider: 'cqg' | 'rithmic';
  /** 'concurrent' → another session took over (actionable take-over); 'error' → rejected/failed logon. */
  kind: 'error' | 'concurrent';
}

/**
 * Cross-provider problem indicator (decision 2026-07-18: "aggregate dot") — the topbar control's dot
 * warns when ANY provider session is in error/concurrent, even for the NON-selected provider, so a
 * Rithmic-selected user still sees their CQG session drop. Returns the first alert (CQG checked
 * first); null when both providers are healthy.
 */
export function deriveAggregateAlert(cqg: ProviderSnapshot, rithmic: ProviderSnapshot): ProviderAlert | null {
  for (const [provider, p] of [['cqg', cqg], ['rithmic', rithmic]] as const) {
    if (p.reason === 'concurrent_session') return { provider, kind: 'concurrent' };
    if (p.display === 'error') return { provider, kind: 'error' };
  }
  return null;
}

export { OFFLINE_SNAPSHOT };
